import { LicenseDeregisterForm } from "@/components/license-deregister-form";
import PageHeader from "@/components/page-header";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import {
  getLicenseDetailsOptions,
  getLicenseTypeConfigurationOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { getLicenseDeregistrationQuestions } from "@/hera-api/deregister-api";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import {
  getQueryErrorHeading,
  getQueryErrorMessage,
  isDeregisterable,
} from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { validate as uuidValidate } from "uuid";

/** Deregistration fee type from license type config (backend may be ahead of generated types). */
type DeregistrationFeeType = "NONE" | "FIXED" | "RE_SUBMISSION";

function getDeregistrationFeeAmount(config: {
  deregistration_fee_type?: DeregistrationFeeType | string | null;
  deregistration_fee?: string | number | null;
  deregistration_initial_fee?: string | number | null;
  deregistration_subsequent_fee?: string | number | null;
}): number | null {
  const type = config.deregistration_fee_type ?? "NONE";
  if (type === "NONE") return null;
  if (type === "FIXED") {
    const v = config.deregistration_fee;
    return v != null ? Number(v) : null;
  }
  const v = config.deregistration_initial_fee;
  return v != null ? Number(v) : null;
}

function hasDeregistrationFee(
  config: Record<string, unknown> | undefined,
): boolean {
  if (!config) return false;
  const amount = getDeregistrationFeeAmount(
    config as Parameters<typeof getDeregistrationFeeAmount>[0],
  );
  return amount != null && amount > 0;
}

export const Route = createFileRoute(
  "/operator/licenses/$licenseId/deregister",
)({
  component: OperatorDeregisterLicenseScreen,
});

function OperatorDeregisterLicenseScreen() {
  const { licenseId } = Route.useParams();
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const isValidLicenseId = uuidValidate(licenseId);
  const navigate = useNavigate();

  const {
    data: licenseData,
    isLoading: isLicenseLoading,
    error: licenseError,
  } = useQuery({
    ...getLicenseDetailsOptions({
      path: { license_id: licenseId },
    }),
    enabled: isAuthSetupComplete && isValidLicenseId,
  });

  const {
    data: configData,
    isLoading: isConfigLoading,
    error: configError,
  } = useQuery({
    ...getLicenseTypeConfigurationOptions({
      path: {
        license_type_id: licenseData?.license_type?.id ?? "",
        jurisdiction_id: licenseData?.jurisdiction?.id ?? "",
      },
    }),
    enabled:
      isAuthSetupComplete &&
      isValidLicenseId &&
      !!licenseData?.license_type?.id &&
      !!licenseData?.jurisdiction?.id,
  });

  const {
    data: questionsData,
    isLoading: isQuestionsLoading,
    error: questionsError,
  } = useQuery({
    queryKey: ["deregistration-questions", licenseId],
    queryFn: () => getLicenseDeregistrationQuestions(licenseId),
    enabled: isAuthSetupComplete && isValidLicenseId && !!licenseData,
  });

  const hasFee = hasDeregistrationFee(
    configData as Record<string, unknown> | undefined,
  );
  const feeAmount = configData
    ? getDeregistrationFeeAmount(
        configData as Parameters<typeof getDeregistrationFeeAmount>[0],
      )
    : null;

  const handleSuccess = () => {
    navigate({ to: "/operator/overview" });
  };

  if (!isValidLicenseId) {
    return (
      <ErrorComponent
        title="Invalid URL"
        message={`The license ID '${licenseId}' is not a valid license ID`}
      />
    );
  }

  if (!isAuthSetupComplete) {
    return <LoadingScreen message="Authenticating..." />;
  }

  if (isLicenseLoading || isConfigLoading || isQuestionsLoading) {
    return <LoadingScreen message="Loading de-registration form..." />;
  }

  if (licenseError || configError || questionsError) {
    return (
      <ErrorComponent
        title={getQueryErrorHeading(
          licenseError || configError || questionsError,
        )}
        message={getQueryErrorMessage(
          licenseError || configError || questionsError,
        )}
      />
    );
  }

  if (!licenseData) {
    return <LoadingScreen message="Loading license..." />;
  }

  if (!isDeregisterable(licenseData)) {
    return (
      <ErrorComponent
        title="License not eligible for de-registration"
        message="De-registration is available for active registrations (including when a renewal balance is due), for Pay Later / unpaid registration, or while pending review. It is not available if a payment is processing or the registration is already de-registered."
      />
    );
  }

  if (!questionsData) {
    return <LoadingScreen message="Loading questions..." />;
  }

  const feeDisplay =
    feeAmount != null ? `$${Number(feeAmount).toFixed(2)}` : "$0.00";
  const subtitle = hasFee
    ? `Enter the effective end date and answer the questions below. A ${feeDisplay} de-registration fee will be required from the registrant after you submit.`
    : "Enter the effective end date and answer the questions below. Then click De-register to complete.";

  return (
    <div className="flex flex-col container mx-auto max-w-3xl space-y-6">
      <PageHeader title="De-register Registration" subtitle={subtitle} />
      <LicenseDeregisterForm
        licenseId={licenseId}
        licenseTypeName={licenseData.license_type.name}
        propertyAddress={licenseData.property?.display_address}
        effectiveEndDateLabel={
          licenseData.license_type.effective_end_date_label ??
          "Effective end date"
        }
        questionsData={questionsData}
        registrantAccountId={licenseData.registrant_account?.id}
        hasDeregistrationFee={hasFee}
        onSuccess={handleSuccess}
      />
    </div>
  );
}
